import { useCallback, useEffect, useRef, useState } from 'react';
import { DateRangeField } from './DateRangeField';
import { FilterBar, FilterQuickSelect, type FilterChip } from './filters/FilterBar';
import { ActionBar } from './ui/ActionBar';
import { CheckRow } from './ui/CheckRow';
import { DenseList, DenseRow } from './ui/DenseList';
import { MetaDisclosure, MetaHeading, MetaList } from './ui/MetaList';
import { RowToggle } from './ui/RowToggle';
import { StatSummary } from './ui/StatSummary';
import { StatusBadge } from './ui/StatusBadge';
import { Button, Field, LoadingPanel, Select } from './ui/primitives';
import { useShellTier } from '../layouts/shellTier';
import { describePeriod, deviceToday } from '../utils/periods';
import { ShiftCashBreakdownPanel } from './ShiftCashBreakdownPanel';
import type { ShiftCashBreakdown } from '../types/shiftCashBreakdown';
import { differenceStatusBadge, shiftCloseStatusBadge } from '../utils/statusTone';
import { getErrorMessage } from '../api/client';
import { getShiftCloseCashBreakdown, getShiftCloseDetail, getShiftCloses, reconcileShiftClose, voidShiftClose } from '../api/shifts';
import { useAuth } from '../auth/AuthProvider';
import { canCreateVoidRequest, getStoreScopeOptions } from '../auth/permissions';
import { Modal } from './Modal';
import { VoidRequestModal } from './VoidRequestModal';
import type { ShiftCloseDetail, ShiftCloseDifferenceStatus, ShiftCloseListFilters, ShiftCloseListItem, ShiftCloseReconciliation, ShiftCloseStatus } from '../types/shifts';
import type { Store } from '../types/stores';
import { formatOptionalMoney, formatQuantity } from '../utils/costFormatting';
import { ResponseDetailView } from './ResponseDetailView';
import { DEFAULT_STORE_TIMEZONE } from '../utils/timezones';
import { nativeCashLabel } from '../utils/shiftCloseCash';
import { useLoyversePresence } from '../utils/loyversePresence';

const PAGE_SIZE = 20;
const VOID_CONFIRM_WORD = 'ANULAR';
const MIN_VOID_REASON_LENGTH = 10;
const STATUS_LABELS: Record<ShiftCloseStatus, string> = { CLOSED: 'Cerrado', VOIDED: 'Anulado' };
const STATUS_OPTIONS = [{ value: '', label: 'Todos' }, { value: 'CLOSED', label: STATUS_LABELS.CLOSED }, { value: 'VOIDED', label: STATUS_LABELS.VOIDED }];

// initialOpenId: abre directo el detalle de un corte (deep-link "Ver Corte" desde una respuesta de
// formulario, hash shifts/<id>).
export function ShiftCloseHistoryPanel({ stores, refreshToken, initialOpenId }: { stores: Store[]; refreshToken: number; initialOpenId?: string }) {
  const { user } = useAuth();
  /**
   * P-075 · DOS estados de filtro, como en Ventas e ingresos: lo aplicado (`filters`, lo que se pidió y lo
   * que usa la paginación) y el borrador del panel «Filtros» (`draftFilters`). Antes los controles
   * escribían directo en lo que leía la paginación: cambiar «Estado» y pulsar «Siguiente» sin aplicar
   * pedía la página con el filtro nuevo y el desplazamiento viejo.
   */
  const [filters, setFilters] = useState<ShiftCloseListFilters>({});
  const [draftFilters, setDraftFilters] = useState<ShiftCloseListFilters>({});
  const [items, setItems] = useState<ShiftCloseListItem[]>([]);
  const [page, setPage] = useState({ total: 0, offset: 0, hasMore: false });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [openShiftCloseId, setOpenShiftCloseId] = useState<string | undefined>(initialOpenId);
  // P-075: solo la respuesta de la ÚLTIMA petición se pinta. Al abrir, la primera sale antes de que lleguen
  // las tiendas (para un gerente, sin tienda: el servidor la rechaza) y su error podía llegar DESPUÉS de la
  // buena y quedarse a la vista; lo mismo al cambiar filtros rápido.
  const loadSeq = useRef(0);

  const load = useCallback(async (nextFilters: ShiftCloseListFilters, offset = 0) => {
    const current = ++loadSeq.current;
    setIsLoading(true);
    setError(undefined);
    try {
      const result = await getShiftCloses({ ...nextFilters, limit: PAGE_SIZE, offset });
      if (current !== loadSeq.current) return;
      setFilters(nextFilters);
      setItems(result.items);
      setPage({ total: result.total, offset: result.offset, hasMore: result.hasMore });
    } catch (loadError) {
      if (current === loadSeq.current) setError(getErrorMessage(loadError, 'No fue posible cargar el historial de cortes.'));
    } finally {
      if (current === loadSeq.current) setIsLoading(false);
    }
  }, []);

  /**
   * La tienda que el ROL impone (un gerente no puede pedir «todas»). Es el filtro con el que arranca el
   * panel: «Limpiar» vuelve a él y no cuenta como filtro puesto por la persona.
   */
  const { allowsAllStores, defaultStoreId: forcedStoreId } = getStoreScopeOptions(user?.role, stores);
  const baseFilters = useCallback((): ShiftCloseListFilters => (forcedStoreId ? { storeId: forcedStoreId } : {}), [forcedStoreId]);

  // Al montar, al cambiar las tiendas y al guardar un corte (refreshToken), el panel vuelve a sus filtros de inicio.
  useEffect(() => {
    const initialFilters = baseFilters();
    setFilters(initialFilters);
    setDraftFilters(initialFilters);
    void load(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, refreshToken, stores, user?.role]);

  const refreshAfterVoid = () => { void load(filters, page.offset); };

  const storeChosenByUser = Boolean(filters.storeId) && filters.storeId !== forcedStoreId;
  const hasDateWindow = Boolean(filters.dateFrom || filters.dateTo);
  const hasActiveFilters = hasDateWindow || storeChosenByUser || Boolean(filters.status) || Boolean(filters.onlyWithDifference);
  const clearFilters = () => {
    const limpio = baseFilters();
    setDraftFilters(limpio);
    void load(limpio, 0);
  };
  /**
   * P-075 · La barra de filtros (patrón de Ventas e ingresos). El periodo, los rápidos y cada «×» se aplican
   * SOBRE lo aplicado; el panel «Filtros» trabaja en borrador y se pide con «Ver resultados». Sin búsqueda:
   * la API de cortes no busca por texto.
   */
  const applyPatch = (patch: Partial<ShiftCloseListFilters>) => { if (!isLoading) void load({ ...filters, ...patch }, 0); };
  const storeNameOf = (id?: string) => stores.find((store) => store.id === id)?.name ?? 'elegida';
  const panelCount = (storeChosenByUser ? 1 : 0) + (filters.status ? 1 : 0) + (filters.onlyWithDifference ? 1 : 0);
  const chips: FilterChip[] = [
    ...(hasDateWindow ? [{ id: 'dates', label: `Fechas: ${describePeriod({ from: filters.dateFrom?.slice(0, 10) ?? '', to: filters.dateTo?.slice(0, 10) ?? '' }, deviceToday())}`, onRemove: () => applyPatch({ dateFrom: undefined, dateTo: undefined }) }] : []),
    ...(storeChosenByUser ? [{ id: 'store', label: `Tienda: ${storeNameOf(filters.storeId)}`, onRemove: () => applyPatch({ storeId: forcedStoreId }) }] : []),
    ...(filters.status ? [{ id: 'status', label: `Estado: ${STATUS_LABELS[filters.status]}`, onRemove: () => applyPatch({ status: undefined }) }] : []),
    ...(filters.onlyWithDifference ? [{ id: 'difference', label: 'Solo con diferencia', onRemove: () => applyPatch({ onlyWithDifference: undefined }) }] : []),
  ];
  const storeOptions = [...(allowsAllStores ? [{ value: '', label: 'Todas las tiendas' }] : []), ...stores.map((store) => ({ value: store.id, label: store.name }))];
  const panelFields = <>
    <Field label="Tienda">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, storeId: event.target.value || undefined })} value={draftFilters.storeId || ''}>
        {storeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </Select>
    </Field>
    <Field label="Estado">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, status: (event.target.value || undefined) as ShiftCloseStatus | undefined })} value={draftFilters.status || ''}>
        {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </Select>
    </Field>
    {/* P-076 (P5): casilla con objetivo táctil de 44 px pegado a su etiqueta. */}
    <CheckRow checked={draftFilters.onlyWithDifference ?? false} label="Solo con diferencia" onChange={(checked) => setDraftFilters({ ...draftFilters, onlyWithDifference: checked || undefined })} plain />
  </>;

  return <section aria-label="Historial de cortes" className="n3-screen">
    {error && <div className="alert alert--error">{error}</div>}

    {/* Ola 3 (D4): orden canónico Fechas → Tienda → filtros adicionales. */}
    <FilterBar
      busy={isLoading}
      chips={chips}
      label="Filtrar el historial de cortes"
      onClear={hasActiveFilters ? clearFilters : undefined}
      panel={{
        applying: isLoading,
        content: panelFields,
        count: panelCount,
        onApply: () => { if (!isLoading) void load({ ...draftFilters, dateFrom: filters.dateFrom, dateTo: filters.dateTo }, 0); },
        onOpen: () => setDraftFilters({ ...filters }),
        onReset: () => setDraftFilters(baseFilters()),
      }}
      period={<DateRangeField disabled={isLoading} from={filters.dateFrom?.slice(0, 10) || ''} onChange={(from, to) => applyPatch({ dateFrom: from || undefined, dateTo: to || undefined })} to={filters.dateTo?.slice(0, 10) || ''} />}
      quick={<>
        {stores.length > 1 && <FilterQuickSelect disabled={isLoading} label="Tienda" onChange={(value) => applyPatch({ storeId: value || undefined })} options={storeOptions} value={filters.storeId || ''} />}
        <FilterQuickSelect disabled={isLoading} label="Estado" onChange={(value) => applyPatch({ status: (value || undefined) as ShiftCloseStatus | undefined })} options={STATUS_OPTIONS} value={filters.status || ''} />
      </>}
      summary={`${page.total} ${page.total === 1 ? 'corte' : 'cortes'}${hasActiveFilters ? ' en lo que estás viendo' : ''}`}
    />

    {isLoading ? <LoadingPanel label="Cargando historial de cortes…" /> : <ShiftCloseListTable hasActiveFilters={hasActiveFilters} items={items} onClearFilters={clearFilters} onOpen={setOpenShiftCloseId} stores={stores} />}
    {!isLoading && page.total > 0 && <div className="coverage-pagination">
      <span>Mostrando {page.offset + 1}–{page.offset + items.length} de {page.total}</span>
      <div>
        <button className="button button--ghost" disabled={page.offset === 0} onClick={() => void load(filters, Math.max(0, page.offset - PAGE_SIZE))} type="button">Anterior</button>
        <button className="button button--ghost" disabled={!page.hasMore} onClick={() => void load(filters, page.offset + PAGE_SIZE)} type="button">Siguiente</button>
      </div>
    </div>}

    {openShiftCloseId && <ShiftCloseDetailModal shiftCloseId={openShiftCloseId} onClose={() => { setOpenShiftCloseId(undefined); if (window.location.hash.startsWith('#shifts/')) window.location.hash = 'shifts'; }} onVoided={refreshAfterVoid} stores={stores} />}
  </section>;
}

function ShiftCloseListTable({ items, onOpen, stores, hasActiveFilters, onClearFilters }: { items: ShiftCloseListItem[]; onOpen: (id: string) => void; stores: Store[]; hasActiveFilters: boolean; onClearFilters: () => void }) {
  const cashLabel = nativeCashLabel(useLoyversePresence() !== false);
  const tier = useShellTier();
  // Acordeón móvil (pedido del dueño): cada tarjeta inicia COLAPSADA (fecha + diferencia + estado);
  // tocar la tarjeta la expande/colapsa. En escritorio no cambia nada (la clase solo estila ≤640px).
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) => setExpandedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  // P-075: con filtros puestos, «no hay cortes» no es lo mismo que «ninguno coincide».
  if (!items.length) return <div className="empty-panel empty-panel--small">{hasActiveFilters
    ? <><strong>Ningún corte coincide con los filtros</strong><span>Prueba con otro periodo, o quita algún filtro.</span><div className="empty-panel__actions"><button className="text-button" onClick={onClearFilters} type="button">Quitar todos los filtros</button></div></>
    : <><strong>Todavía no hay cortes guardados</strong><span>Aparecerán aquí cuando guardes un corte de turno.</span></>}</div>;
  // P-076: en el teléfono, una fila compacta por corte: el resultado (diferencia con su insignia y el estado) y
  // las cifras principales (esperado, contado, venta) a la vista; la metadata (empleado, rango, quién cerró y
  // cuándo, tickets, ventas en efectivo, gastos) detrás de «Ver detalles». Son los mismos 15 datos de la tabla.
  // En tableta y escritorio sigue la tabla de siempre.
  if (tier === 'compact') return <DenseList label="Historial de cortes" separated>{items.map((item) => {
    const timeZone = stores.find((store) => store.name === item.storeName)?.timezone ?? DEFAULT_STORE_TIMEZONE;
    return <DenseRow
      actions={<Button onClick={() => onOpen(item.id)} variant="ghost">Ver detalle</Button>}
      badge={<>
        <DifferenceStatusBadge status={item.differenceStatus} />
        <ShiftCloseStatusPill status={item.status} />
        {item.hasWarnings && <span title="Este corte tiene advertencias guardadas">⚠ Con advertencias</span>}
        {item.status !== 'VOIDED' && item.hasPendingVoidRequest && <span className="consumption-status consumption-status--pending">Solicitud pendiente</span>}
        {item.status === 'CLOSED' && item.hasApprovedChangeRequest && <span className="consumption-status consumption-status--pending">Cambio aprobado — anular y recapturar</span>}
      </>}
      detailsLabel="Más datos"
      details={<MetaList items={[
        { label: 'Empleado', value: item.employeeName || 'Sin asignar' },
        { label: 'Rango', value: `${formatTime(item.startAt, timeZone)} – ${formatTime(item.endAt, timeZone)}` },
        { label: 'Cerró', value: item.closedByName || 'Sin usuario' },
        { label: 'Cerrado el', value: formatDateTime(item.closedAt, timeZone) },
        { label: 'Tickets', value: item.totalReceipts },
        { label: cashLabel, value: formatOptionalMoney(item.otherSalesCashTotal) },
        { label: 'Gastos', value: formatOptionalMoney(item.otherExpensesTotal) },
      ]} />}
      figures={[
        { label: 'Esperado', value: formatOptionalMoney(item.expectedCash) },
        { label: 'Contado', value: formatOptionalMoney(item.countedCash) },
        { label: 'Venta', value: formatOptionalMoney(item.grossSales) },
      ]}
      flagged={item.status !== 'VOIDED' && item.hasPendingVoidRequest}
      inactive={item.status === 'VOIDED' || item.hasApprovedChangeRequest}
      key={item.id}
      name={item.folio || `corte del ${formatDate(item.startAt, timeZone)}`}
      subtitle={item.storeName}
      title={`${item.folio ? `${item.folio} · ` : ''}${formatDate(item.startAt, timeZone)}`}
      value={formatOptionalMoney(item.cashDifference)}
      valueTone={differenceTone(item.differenceStatus)}
    />;
  })}</DenseList>;
  // Móvil (≤640px): data-table--cards convierte cada corte en tarjeta (data-label = etiqueta).
  // Estados de un vistazo (pedido del dueño): ANULADO = atenuado (mismo gris de registros
  // cancelados); con SOLICITUD PENDIENTE = fondo ámbar suave (mismo tono de los pills pendientes).
  // P-075 (S9): `RowToggle` abre la tarjeta con el teclado y dice si está abierta.
  return <div className="data-table-wrap"><table className="data-table shift-close-table data-table--cards n3-has-row-toggle"><thead><tr><th>Fecha</th><th>Tienda</th><th>Empleado</th><th>Rango</th><th>Cerró</th><th>Cerrado el</th><th>Tickets</th><th>Venta bruta</th><th>{cashLabel}</th><th>Gastos</th><th>Efectivo esperado</th><th>Efectivo contado</th><th>Diferencia</th><th>Estado</th><th>Acción</th></tr></thead><tbody>{items.map((item) => { const timeZone = stores.find((store) => store.name === item.storeName)?.timezone ?? DEFAULT_STORE_TIMEZONE; return <tr className={`${item.status === 'VOIDED' || item.hasApprovedChangeRequest ? 'data-row--inactive' : item.hasPendingVoidRequest ? 'data-row--flagged' : ''} ${expandedIds.has(item.id) ? 'dt-card--open' : 'dt-card--closed'}`} onClick={() => toggleExpanded(item.id)} key={item.id}>
    <td className="dt-card-main"><strong>{item.folio ? `${item.folio} · ` : ''}{formatDate(item.startAt, timeZone)}</strong><small>{item.storeName}</small><RowToggle expanded={expandedIds.has(item.id)} label={item.folio || `corte del ${formatDate(item.startAt, timeZone)}`} onToggle={() => toggleExpanded(item.id)} /></td>
    <td className="dt-hide-m">{item.storeName}</td>
    <td data-label="Empleado">{item.employeeName || 'Sin asignar'}</td>
    <td data-label="Rango">{formatTime(item.startAt, timeZone)} – {formatTime(item.endAt, timeZone)}</td>
    <td data-label="Cerró">{item.closedByName || 'Sin usuario'}</td>
    <td data-label="Cerrado el">{formatDateTime(item.closedAt, timeZone)}</td>
    <td data-label="Tickets">{item.totalReceipts}</td>
    <td data-label="Venta bruta">{formatOptionalMoney(item.grossSales)}</td>
    <td data-label={cashLabel}>{formatOptionalMoney(item.otherSalesCashTotal)}</td>
    <td data-label="Gastos">{formatOptionalMoney(item.otherExpensesTotal)}</td>
    <td data-label="Efectivo esperado">{formatOptionalMoney(item.expectedCash)}</td>
    <td data-label="Efectivo contado">{formatOptionalMoney(item.countedCash)}</td>
    <td className="dt-card-keep" data-label="Diferencia">{formatOptionalMoney(item.cashDifference)} <DifferenceStatusBadge status={item.differenceStatus} /></td>
    <td className="dt-card-keep" data-label="Estado"><ShiftCloseStatusPill status={item.status} />{item.hasWarnings && <span title="Este corte tiene advertencias guardadas"> ⚠</span>}{item.status !== 'VOIDED' && item.hasPendingVoidRequest && <span className="consumption-status consumption-status--pending">Solicitud pendiente</span>}{item.status === 'CLOSED' && item.hasApprovedChangeRequest && <span className="consumption-status consumption-status--pending">Cambio aprobado — anular y recapturar</span>}</td>
    <td className="dt-card-actions dt-card-keep"><button className="text-button" onClick={(event) => { event.stopPropagation(); onOpen(item.id); }} type="button">Ver detalle</button></td>
  </tr>; })}</tbody></table></div>;
}

// Exportado: también se abre EN SITIO desde el detalle de una respuesta de formulario (Ver Corte).
export function ShiftCloseDetailModal({ shiftCloseId, onClose, onVoided, stores }: { shiftCloseId: string; onClose: () => void; onVoided: () => void; stores: Store[] }) {
  // Codex R2 · H3: las filas compactas solo en el teléfono; en tableta y escritorio, las tablas de siempre.
  const tier = useShellTier();
  const { user } = useAuth();
  // P-075: el efectivo de lo vendido en Negocio360 se nombra igual que en la pantalla del corte.
  const loyverseConnected = useLoyversePresence();
  const canVoid = user?.role !== 'EMPLOYEE';
  const [detail, setDetail] = useState<ShiftCloseDetail>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [isVoidModalOpen, setIsVoidModalOpen] = useState(false);
  const [isVoidRequestModalOpen, setIsVoidRequestModalOpen] = useState(false);
  const [voidNotice, setVoidNotice] = useState<string>();
  const [copyText, setCopyText] = useState<string>();
  const [copyNotice, setCopyNotice] = useState<string>();
  const [reconciliation, setReconciliation] = useState<ShiftCloseReconciliation>();
  const [isReconciling, setIsReconciling] = useState(false);
  // Detalle del turno histórico (solo lectura; nunca reescribe el corte guardado).
  const [breakdown, setBreakdown] = useState<ShiftCashBreakdown>();
  useEffect(() => {
    let alive = true;
    getShiftCloseCashBreakdown(shiftCloseId)
      .then((result) => { if (alive) setBreakdown(result); })
      .catch(() => { /* el detalle nunca rompe el corte: si falla, simplemente no se muestra */ });
    return () => { alive = false; };
  }, [shiftCloseId]);
  const [reconcileError, setReconcileError] = useState<string>();
  // Espejo del "Ver Corte" de las respuestas: desde el checklist del corte se abre la RESPUESTA
  // completa en sitio (modal encima); al cerrarla sigues en el corte.
  const [openSubmissionId, setOpenSubmissionId] = useState<string | null>(null);
  const detailTimeZone = stores.find((store) => store.name === detail?.storeName)?.timezone ?? DEFAULT_STORE_TIMEZONE;

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);
    try {
      const result = await getShiftCloseDetail(shiftCloseId);
      setDetail(result.shiftClose);
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'No fue posible cargar el detalle del corte.'));
    } finally {
      setIsLoading(false);
    }
  }, [shiftCloseId]);

  useEffect(() => { void load(); }, [load]);

  const copySummary = async () => {
    if (!detail) return;
    const text = buildSummaryText(detail, stores.find((store) => store.name === detail.storeName)?.timezone ?? DEFAULT_STORE_TIMEZONE, nativeCashLabel(loyverseConnected !== false));
    setCopyNotice(undefined);
    try {
      await navigator.clipboard.writeText(text);
      setCopyNotice('Resumen copiado al portapapeles.');
      setCopyText(undefined);
    } catch {
      setCopyText(text);
    }
  };

  const reviewChanges = async () => {
    if (isReconciling) return;
    setIsReconciling(true);
    setReconcileError(undefined);
    try {
      const result = await reconcileShiftClose(shiftCloseId);
      setReconciliation(result);
    } catch (reconcileErr) {
      setReconcileError(getErrorMessage(reconcileErr, 'No fue posible revisar los cambios de este corte.'));
    } finally {
      setIsReconciling(false);
    }
  };

  const timeZone = detail ? stores.find((store) => store.name === detail.storeName)?.timezone ?? DEFAULT_STORE_TIMEZONE : DEFAULT_STORE_TIMEZONE;

  // P-076: la hoja dice de qué corte es en su descripción (tienda y rango); el cuerpo abre con el resultado.
  const description = detail ? `${detail.folio ? `${detail.folio} · ` : ''}${detail.storeName} · ${formatDateTime(detail.startAt, timeZone)} – ${formatDateTime(detail.endAt, timeZone)}` : undefined;

  return <Modal description={description} title="Detalle del corte" isBusy={false} onClose={onClose} size="wide">
    {error && <div className="alert alert--error">{error}</div>}
    {isLoading && <div className="inline-loading"><span className="spinner" />Cargando detalle...</div>}
    {detail && !isLoading && <>
      {/* P-076: primero el resultado y el estado (las cuatro cifras que se vienen a ver), después las cifras
          del turno con su etiqueta, y la metadata administrativa plegada. Son los mismos 24 datos de antes,
          en otro orden y con otro peso; ninguno se quita. */}
      <StatSummary
        label="Resultado del corte"
        stats={[
          { label: 'Diferencia', value: formatOptionalMoney(detail.cashDifference), tone: differenceTone(detail.differenceStatus), hint: <DifferenceStatusBadge status={detail.differenceStatus} /> },
          { label: 'Efectivo esperado', value: formatOptionalMoney(detail.expectedCash) },
          { label: 'Efectivo contado', value: formatOptionalMoney(detail.countedCash) },
          { label: 'Estado', value: <ShiftCloseStatusPill status={detail.status} /> },
        ]}
      />
      <MetaHeading>Cifras del turno</MetaHeading>
      <MetaList
        aria-label="Cifras del turno"
        columns
        items={[
          { label: 'Venta bruta Loyverse', value: formatOptionalMoney(detail.grossSales) },
          { label: 'Venta neta Loyverse', value: formatOptionalMoney(detail.netSales) },
          { label: 'Ventas Loyverse en efectivo', value: formatOptionalMoney(detail.loyverseCashTotal) },
          { label: nativeCashLabel(loyverseConnected !== false), value: formatOptionalMoney(detail.otherSalesCashTotal) },
          { label: 'Gastos', value: formatOptionalMoney(detail.otherExpensesTotal) },
          { label: 'Compras', value: formatOptionalMoney(detail.purchasesTotal) },
          { label: 'Caja inicial', value: formatOptionalMoney(detail.cashOpen) },
          ...(detail.depositApplies ? [{ label: 'Depósito', value: `${formatOptionalMoney(detail.depositAmount)} (${detail.depositAccount || 'Sin cuenta'})` }] : []),
        ]}
      />
      {/* Las notas y la anulación no son metadata: quien revisa el corte tiene que verlas sin abrir nada. */}
      {(detail.notes || detail.voidedAt || detail.voidReason) && <MetaList
        aria-label="Notas y anulación"
        items={[
          ...(detail.notes ? [{ label: 'Notas', value: detail.notes }] : []),
          ...(detail.voidedAt ? [{ label: 'Anulado', value: `${formatDateTime(detail.voidedAt, timeZone)} por ${detail.voidedByName || 'Sin usuario'}` }] : []),
          ...(detail.voidReason ? [{ label: 'Motivo de anulación', value: detail.voidReason }] : []),
        ]}
        stack
      />}
      <MetaDisclosure
        columns
        items={[
          ...(detail.folio ? [{ label: 'Folio', value: detail.folio }] : []),
          { label: 'Tienda', value: detail.storeName },
          { label: 'Empleado responsable', value: detail.employeeName || 'Sin asignar' },
          { label: 'Rango', value: `${formatDateTime(detail.startAt, timeZone)} – ${formatDateTime(detail.endAt, timeZone)}` },
          { label: 'Cerrado por', value: detail.closedByName || 'Sin usuario' },
          { label: 'Cerrado el', value: formatDateTime(detail.closedAt, timeZone) },
          { label: 'Tickets', value: detail.totalReceipts },
          { label: 'Líneas de venta', value: detail.totalSaleLines },
          { label: 'Productos vendidos', value: formatQuantity(detail.totalItemsSold) },
        ]}
        label="Ver más datos del corte"
      />

      {/* Detalle del turno del corte YA GUARDADO: totales del snapshot inmutable + detalle
          reconstruido y reconciliado (si algo se editó después, el panel lo declara). */}
      {breakdown && <ShiftCashBreakdownPanel breakdown={breakdown} />}

      {detail.status === 'CLOSED' && detail.hasApprovedChangeRequest && <div className="alert alert--warning">Este corte tiene una <strong>modificación aprobada</strong>: anúlalo (botón al final) y captúrenlo de nuevo con los datos correctos.</div>}
      {detail.warnings.length > 0 && <div className="alert alert--warning">{detail.warnings.join(' ')}</div>}
      {voidNotice && <div className="alert alert--success">{voidNotice}</div>}
      {copyNotice && <div className="alert alert--success">{copyNotice}</div>}
      {copyText && <label className="field"><span>No fue posible copiar automáticamente. Selecciona y copia el texto:</span><textarea onFocus={(event) => event.target.select()} readOnly rows={9} value={copyText} /></label>}

      <MetaHeading>Métodos de pago</MetaHeading>
      {detail.paymentBreakdown.length ? <div className="data-table-wrap"><table className="data-table"><thead><tr><th>Método</th><th>Monto</th><th>Tickets</th></tr></thead><tbody>{detail.paymentBreakdown.map((payment, index) => <tr key={index}>
        <td>{payment.paymentType}</td>
        <td>{formatOptionalMoney(payment.amount)}</td>
        <td>{payment.count}</td>
      </tr>)}</tbody></table></div> : <div className="empty-panel empty-panel--small"><strong>Sin desglose de métodos de pago</strong></div>}

      <MetaHeading>Productos más vendidos</MetaHeading>
      {/* P-076: en el teléfono, filas compactas (nombre + cantidad + importe) en vez de una tabla que dejaba Tickets fuera de la hoja. */}
      {detail.productSummary.length ? (tier === 'compact' ? <DenseList label="Productos más vendidos" plain>{detail.productSummary.map((product, index) => <DenseRow
        key={index}
        meta={[`Cantidad: ${formatQuantity(product.quantity)}`, `Tickets: ${product.receiptsCount}`]}
        subtitle={product.variantName || undefined}
        title={product.itemName}
        value={formatOptionalMoney(product.grossSales)}
      />)}</DenseList> : <div className="data-table-wrap"><table className="data-table"><thead><tr><th>Producto</th><th>Cantidad</th><th>Venta bruta</th><th>Tickets</th></tr></thead><tbody>{detail.productSummary.map((product, index) => <tr key={index}>
        <td><strong>{product.itemName}</strong>{product.variantName && <small>{product.variantName}</small>}</td>
        <td>{formatQuantity(product.quantity)}</td>
        <td>{formatOptionalMoney(product.grossSales)}</td>
        <td>{product.receiptsCount}</td>
      </tr>)}</tbody></table></div>) : <div className="empty-panel empty-panel--small"><strong>Sin productos vendidos en este rango</strong></div>}

      {detail.inventoryPostingSummary && <>
        <MetaHeading>Inventario descontado por ventas</MetaHeading>
        <StatSummary
          label="Inventario descontado en este rango"
          plain
          stats={[
            { label: 'Corridas relacionadas', value: detail.inventoryPostingSummary.runsCount },
            { label: 'Salidas de inventario', value: detail.inventoryPostingSummary.totalTransactionsCreated },
            ...(detail.inventoryPostingSummary.totalRealCostDeducted !== undefined ? [{ label: 'Costo real descontado', value: formatOptionalMoney(detail.inventoryPostingSummary.totalRealCostDeducted) }] : []),
            ...(detail.inventoryPostingSummary.hasReversedRuns ? [{ label: 'Reversas', value: 'Sí hay corridas revertidas', tone: 'warning' as const }] : []),
          ]}
        />
      </>}

      {detail.forms && detail.forms.length > 0 && <>
        <MetaHeading>Información operativa — Checklist del turno</MetaHeading>
        {/* P-076: en el teléfono, lista vertical con «Ver respuesta» siempre a la vista (en la tabla quedaba fuera de la hoja). */}
        {tier === 'compact' ? <DenseList label="Checklist del turno" plain>{detail.forms.map((f) => <DenseRow
          actions={f.status === 'SUBMITTED' && <Button onClick={() => setOpenSubmissionId(f.submissionId)} variant="ghost">Ver respuesta</Button>}
          badge={<span className={`form-badge ${f.status === 'SUBMITTED' ? 'form-badge--active' : 'form-badge--draft'}`}>{f.status === 'SUBMITTED' ? 'Enviado' : 'Borrador'}</span>}
          key={f.submissionId}
          meta={[`Respondió: ${f.respondentName}`, `Enviado: ${f.submittedAt ? formatDateTime(f.submittedAt, detailTimeZone) : '—'}`]}
          title={<>{f.formName} <small>v{f.versionNumber}</small></>}
        />)}</DenseList> : <div className="data-table-wrap"><table className="data-table"><thead><tr><th>Formulario</th><th>Respondió</th><th>Enviado</th><th>Estado</th><th></th></tr></thead><tbody>{detail.forms.map((f) => <tr key={f.submissionId}>
          <td><strong>{f.formName}</strong> <small>v{f.versionNumber}</small></td>
          <td>{f.respondentName}</td>
          <td>{f.submittedAt ? formatDateTime(f.submittedAt, detailTimeZone) : '—'}</td>
          <td><span className={`form-badge ${f.status === 'SUBMITTED' ? 'form-badge--active' : 'form-badge--draft'}`}>{f.status === 'SUBMITTED' ? 'Enviado' : 'Borrador'}</span></td>
          <td>{f.status === 'SUBMITTED' && <button className="text-button" onClick={() => setOpenSubmissionId(f.submissionId)} type="button">Ver respuesta</button>}</td>
        </tr>)}</tbody></table></div>}
      </>}

      {/* Acciones al FONDO del detalle (pedido del dueño): primero se lee el corte, al final se actúa.
          P-076: las mismas acciones, en el mismo sitio y con las mismas condiciones; solo cambia el orden visual
          (Copiar · Imprimir · Revisar juntas) y lo destructivo va aparte, en texto rojo. */}
      <ActionBar
        aria-label="Acciones del corte"
        danger={detail.status === 'CLOSED' && canVoid ? <Button onClick={() => setIsVoidModalOpen(true)} variant="danger">Anular corte</Button> : undefined}
        note={detail.status === 'VOIDED' ? 'Este corte ya fue anulado.'
          : detail.status === 'CLOSED' && detail.hasPendingVoidRequest ? 'Ya solicitaste un cambio de este corte — está pendiente de revisión.'
            : detail.status === 'CLOSED' && !canVoid && (!canCreateVoidRequest(user?.role) || detail.closedByUserId !== user?.id) ? 'No tienes permiso para anular cortes.'
              : undefined}
      >
        <Button onClick={() => void copySummary()} variant="secondary">Copiar resumen</Button>
        <Button onClick={() => window.print()} variant="secondary">Imprimir</Button>
        {detail.status === 'CLOSED' && <Button disabled={isReconciling} onClick={() => void reviewChanges()} variant="ghost">{isReconciling ? 'Revisando...' : 'Revisar cambios después del cierre'}</Button>}
        {detail.status === 'CLOSED' && !canVoid && canCreateVoidRequest(user?.role) && detail.closedByUserId === user?.id && !detail.hasPendingVoidRequest
          && <Button onClick={() => setIsVoidRequestModalOpen(true)} variant="secondary">Solicitar anulación o cambio</Button>}
      </ActionBar>

      {reconcileError && <div className="alert alert--error">{reconcileError}</div>}
      {reconciliation && <div className={`alert alert--${reconciliation.isDifferent ? 'warning' : 'success'}`}>
        {reconciliation.message}
        {reconciliation.isDifferent && <> Considera anular y crear un nuevo corte si corresponde.
          <ul>
            <li>Venta bruta guardada: {formatOptionalMoney(reconciliation.snapshotGrossSales)} → actual: {formatOptionalMoney(reconciliation.currentGrossSales)}</li>
            <li>Tickets guardados: {reconciliation.snapshotTotalReceipts} → actuales: {reconciliation.currentTotalReceipts}</li>
          </ul>
        </>}
      </div>}
    </>}

    {isVoidModalOpen && detail && <VoidShiftCloseModal
      shiftCloseId={detail.id}
      onClose={() => setIsVoidModalOpen(false)}
      onVoided={() => { setVoidNotice('Corte anulado correctamente. No se modificaron ventas ni inventario.'); setIsVoidModalOpen(false); void load(); onVoided(); }}
    />}
    {isVoidRequestModalOpen && detail && <VoidRequestModal
      targetType="SHIFT_CLOSE"
      targetId={detail.id}
      targetSummary={`Corte de turno — ${detail.storeName}`}
      onClose={() => setIsVoidRequestModalOpen(false)}
      onSubmitted={(message) => { setVoidNotice(message); setIsVoidRequestModalOpen(false); }}
    />}
    {openSubmissionId && <Modal title="Respuesta del formulario" onClose={() => setOpenSubmissionId(null)} size="wide" footer={<button className="button button--ghost" onClick={() => setOpenSubmissionId(null)} type="button">Cerrar</button>}>
      {/* canOpenShiftClose=false: ya estás viendo ese corte — evita abrir corte→respuesta→corte→… */}
      <ResponseDetailView submissionId={openSubmissionId} stores={stores} timeZone={detailTimeZone} canOpenShiftClose={false} backLabel="← Volver al corte" onBack={() => setOpenSubmissionId(null)} />
    </Modal>}
  </Modal>;
}

function VoidShiftCloseModal({ shiftCloseId, onClose, onVoided }: { shiftCloseId: string; onClose: () => void; onVoided: () => void }) {
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [isVoiding, setIsVoiding] = useState(false);
  const [error, setError] = useState<string>();

  const canConfirm = confirmText === VOID_CONFIRM_WORD && reason.trim().length >= MIN_VOID_REASON_LENGTH;

  const confirmVoid = async () => {
    if (!canConfirm || isVoiding) return;
    setIsVoiding(true);
    setError(undefined);
    try {
      await voidShiftClose(shiftCloseId, { reason: reason.trim(), confirmationText: confirmText, confirm: true });
      onVoided();
    } catch (voidError) {
      setError(getErrorMessage(voidError, 'No fue posible anular este corte.'));
    } finally {
      setIsVoiding(false);
    }
  };

  return <Modal
    title="Confirmar anulación de corte"
    isBusy={isVoiding}
    onClose={onClose}
    footer={<>
      <button className="button button--ghost" disabled={isVoiding} onClick={onClose} type="button">Cancelar</button>
      <button className="button button--danger" disabled={isVoiding || !canConfirm} onClick={() => void confirmVoid()} type="button">{isVoiding ? 'Anulando...' : 'Confirmar anulación'}</button>
    </>}
  >
    {error && <div className="alert alert--error">{error}</div>}
    <div className="alert alert--warning">Anular este corte no modifica ventas ni inventario. Solo marca el corte como anulado y conserva el historial.</div>
    <label className="field">
      <span>Motivo de la anulación (mínimo 10 caracteres)</span>
      <textarea onChange={(event) => setReason(event.target.value)} rows={3} value={reason} />
    </label>
    <label className="field">
      <span>Escribe {VOID_CONFIRM_WORD} para confirmar</span>
      <input onChange={(event) => setConfirmText(event.target.value)} placeholder={VOID_CONFIRM_WORD} value={confirmText} />
    </label>
  </Modal>;
}

// Ola 4 (D6): estado→StatusBadge canónico (mapeo compartido en utils/statusTone).
function ShiftCloseStatusPill({ status }: { status: ShiftCloseStatus }) {
  const view = shiftCloseStatusBadge(status);
  return <StatusBadge variant={view.variant}>{view.label}</StatusBadge>;
}

/** P-076: el tono de la cifra «Diferencia» sigue al de su insignia (faltante en rojo, sobrante en ámbar). */
function differenceTone(status: ShiftCloseDifferenceStatus): 'default' | 'danger' | 'warning' | 'success' {
  const variant = differenceStatusBadge(status).variant;
  return variant === 'danger' || variant === 'warning' || variant === 'success' ? variant : 'default';
}

function DifferenceStatusBadge({ status }: { status: ShiftCloseDifferenceStatus }) {
  // Ola 4: Faltante ahora pide atención (rojo) — antes se veía gris, como si no pasara nada.
  const view = differenceStatusBadge(status);
  return <StatusBadge variant={view.variant}>{view.label}</StatusBadge>;
}

function buildSummaryText(detail: ShiftCloseDetail, timeZone: string = DEFAULT_STORE_TIMEZONE, cashLabel = nativeCashLabel(true)): string {
  return [
    'Corte de turno',
    `Tienda: ${detail.storeName}`,
    `Empleado responsable: ${detail.employeeName || 'Sin asignar'}`,
    `Rango: ${formatDateTime(detail.startAt, timeZone)} – ${formatDateTime(detail.endAt, timeZone)}`,
    `Cerrado por: ${detail.closedByName || 'Sin usuario'}`,
    `Venta bruta Loyverse: ${formatOptionalMoney(detail.grossSales)}`,
    `${cashLabel}: ${formatOptionalMoney(detail.otherSalesCashTotal)}`,
    `Gastos: ${formatOptionalMoney(detail.otherExpensesTotal)}`,
    `Compras: ${formatOptionalMoney(detail.purchasesTotal)}`,
    `Efectivo esperado: ${formatOptionalMoney(detail.expectedCash)}`,
    `Efectivo contado: ${formatOptionalMoney(detail.countedCash)}`,
    `Diferencia: ${formatOptionalMoney(detail.cashDifference)}`,
    `Estado: ${detail.status === 'CLOSED' ? 'Corte cerrado' : 'Corte anulado'}`,
    `Notas: ${detail.notes || 'Sin notas'}`,
  ].join('\n');
}

function formatDateTime(value: string, timeZone: string = DEFAULT_STORE_TIMEZONE) { return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(value)); }
// Solo fecha (día): la columna "Fecha" muestra el DÍA DEL TURNO (startAt), no cuándo se cerró.
function formatDate(value: string, timeZone: string = DEFAULT_STORE_TIMEZONE) { return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeZone }).format(new Date(value)); }
function formatTime(value: string, timeZone: string = DEFAULT_STORE_TIMEZONE) { return new Intl.DateTimeFormat('es-MX', { timeStyle: 'short', timeZone }).format(new Date(value)); }
